import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from 'react';
import { Settings } from '../../../icons';
import { useDevice } from '../../../providers/Device';
import { getTestIds } from '../../../utils/TestIds';
import { DropdownMenu } from '../../DropdownMenu';
import { IconButton } from '../../IconButton';
import { Column, Row } from '../../Layout';
import { PaginationControl } from '../../PaginationControl';
import { Skeleton } from '../../Skeleton';
import { Tooltip } from '../../Tooltip';
import { applyDroppedOrder, applyToggleFrozen, emptyColumnsState } from '../tableCore/columns/columnsState';
import { useColumnFloors } from '../tableCore/columns/useColumnFloors';
import { columnsByName, toMobileCardFields } from '../tableCore/mobileCard/mobileCard';
import { COLUMN_HEADER_CHROME_ENABLED, ROW_HEIGHT, SETTINGS_ICON_SIZE, MOBILE_SKELETON_CARD_HEIGHT, SKELETON_ROW_COUNT, SKELETON_ROW_GAP, SKELETON_ROW_STAGGER_MS, SEPARATED_ROW_GAP, VIRTUAL_OVERSCAN_ROWS, VIRTUAL_UNMEASURED_ROWS } from '../tableCore/table.constants';
import styles from '../tableCore/table.module.scss';
import { TableHeader } from '../tableCore/TableHeader';
import { TableMobileCards } from '../tableCore/TableMobileCards/TableMobileCards';
import { TableRow } from '../tableCore/TableRow';
import { TableSurface } from '../tableCore/TableSurface/TableSurface';
import { useTableLayout } from '../tableCore/useTableLayout/useTableLayout';
import { ColumnsPanel } from './columns/ColumnsPanel';
import { useColumnInteractions } from './columns/useColumnInteractions';
import { sortItems } from './DataTable.comparators';
import { nextSort, toggledSelection, virtualRowWindow, withMainSheetHeader } from './DataTable.utils';
export function DataTable(props) {
    var _ref;
    const { mainColumn, columns, items, getRowId, mobile, columnsPanel, pagination, columnsState: callerColumnsState, onColumnsStateChange: onCallerColumnsStateChange, allowSorting = true, allowColumnHide = false, rowActions, trailingColumn, allowColumnReorder = false, allowColumnResize = false, columnMenu, reorderBi, sort, selection, rowStyle = 'separated', state = 'default', onRowClick, rowHeapId, rowIntent, isRowEntering, emptyState, rowHref, onCellChange, getCellError, virtualized = false, testId } = props;
    const now = props.now;
    const testIds = useMemo(()=>getTestIds(testId), [
        testId
    ]);
    const { isMobileDevice } = useDevice();
    const [ownColumnsState, setOwnColumnsState] = useState(emptyColumnsState);
    const columnsState = callerColumnsState !== null && callerColumnsState !== void 0 ? callerColumnsState : ownColumnsState;
    const onColumnsStateChange = onCallerColumnsStateChange !== null && onCallerColumnsStateChange !== void 0 ? onCallerColumnsStateChange : setOwnColumnsState;
    const [tableSort, setTableSort] = useState();
    const [editingCell, setEditingCell] = useState();
    const [pendingRowIds, setPendingRowIds] = useState(new Set());
    const [isPaging, setIsPaging] = useState(false);
    const [scrollTop, setScrollTop] = useState(0);
    const trackPending = useCallback((rowId, committed)=>{
        if (!(committed instanceof Promise)) return committed;
        setPendingRowIds((prev)=>new Set(prev).add(rowId));
        const clear = ()=>setPendingRowIds((prev)=>{
                const next = new Set(prev);
                next.delete(rowId);
                return next;
            });
        // WHY: defer to a macrotask so the settled data's re-render commits before the loader clears, avoiding a stale-cell flash.
        const clearAfterRender = ()=>setTimeout(clear, 0);
        committed.then(clearAfterRender, clearAfterRender);
        return committed;
    }, []);
    const requestPage = pagination === null || pagination === void 0 ? void 0 : pagination.onPageChange;
    const changePage = useCallback(async (page)=>{
        if (requestPage == null || isPaging) return;
        const pending = requestPage(page);
        if (pending == null) return;
        setIsPaging(true);
        try {
            await pending;
        } finally{
            setIsPaging(false);
        }
    }, [
        requestPage,
        isPaging
    ]);
    const { floors, measurer } = useColumnFloors({
        mainColumn,
        columns,
        trailingColumn,
        sampleTestId: testIds.floorSample
    });
    const { scrollerRef, containerHeight, columns: resolved, trailingColumn: resolvedTrailingColumn, frozenOffsets, frozenPrefixLength, minWidth, isScrolledX, isScrolledY, hasColumnsBeyondEnd, hasRowsBelow, onScroll } = useTableLayout({
        mainColumn,
        columns,
        trailingColumn,
        columnsState,
        floors,
        hasActions: rowActions != null,
        hasSelection: selection != null,
        framed: rowStyle === 'unified'
    });
    const bodyColumns = resolved;
    const effectiveState = isPaging ? 'loading' : state;
    const effectiveSort = (sort === null || sort === void 0 ? void 0 : sort.sortedBy) === 'table' ? tableSort !== null && tableSort !== void 0 ? tableSort : sort : sort;
    const byName = useMemo(()=>columnsByName(mainColumn != null ? [
            mainColumn,
            ...columns
        ] : columns), [
        mainColumn,
        columns
    ]);
    const mobileCardFields = useMemo(()=>toMobileCardFields({
            byName,
            fields: mobile.fields,
            mainColumn
        }), [
        byName,
        mobile.fields,
        mainColumn
    ]);
    const cardRowActions = useMemo(()=>rowActions == null ? undefined : withMainSheetHeader(rowActions, mainColumn), [
        rowActions,
        mainColumn
    ]);
    const rowEditingFor = (row, rowId)=>onCellChange == null ? undefined : {
            editingColumnId: (editingCell === null || editingCell === void 0 ? void 0 : editingCell.rowId) === rowId ? editingCell.columnId : undefined,
            getCellError: (columnId)=>getCellError === null || getCellError === void 0 ? void 0 : getCellError(row, columnId),
            onStart: (columnId)=>setEditingCell({
                    rowId,
                    columnId
                }),
            onCommit: (columnId, value)=>{
                const committed = onCellChange({
                    row,
                    columnId,
                    value
                });
                setEditingCell(undefined);
                return trackPending(rowId, committed);
            },
            onCancel: ()=>setEditingCell(undefined)
        };
    const interactions = useColumnInteractions({
        columns: bodyColumns,
        columnsState,
        onColumnsStateChange
    });
    const visibleItems = useMemo(()=>effectiveSort != null && (sort === null || sort === void 0 ? void 0 : sort.sortedBy) === 'table' ? sortItems({
            items,
            column: resolved.find((column)=>column.id === effectiveSort.name),
            direction: effectiveSort.direction
        }) : items, [
        items,
        sort,
        effectiveSort,
        resolved
    ]);
    const pageControls = pagination == null || pagination.total <= pagination.pageSize ? null : /*#__PURE__*/ _jsx(Row, {
        justifyContent: "start",
        className: styles.pageControls,
        children: /*#__PURE__*/ _jsx(PaginationControl, {
            compact: true,
            pageNumber: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onPageNumberChange: changePage,
            testId: testIds.pagination()
        })
    });
    if (isMobileDevice) {
        const showsRows = effectiveState !== 'loading' && state !== 'empty';
        return /*#__PURE__*/ _jsxs(TableMobileCards, {
            items: showsRows ? visibleItems : [],
            getRowId: getRowId,
            fields: mobileCardFields,
            gray: mobile.cardSkin === 'gray',
            cardVariant: "list",
            rowActions: cardRowActions,
            trailingColumn: trailingColumn,
            now: now,
            editingFor: rowEditingFor,
            isPending: (rowId)=>pendingRowIds.has(rowId),
            testId: testId,
            children: [
                effectiveState === 'loading' && Array.from({
                    length: SKELETON_ROW_COUNT
                }, (_, index)=>// WHY: a phone lists cards on the page's grey ground, where the light skeleton disappears.
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: styles.skeletonRow,
                        height: MOBILE_SKELETON_CARD_HEIGHT,
                        testId: testIds.skeleton(index)
                    }, index)),
                effectiveState === 'empty' && /*#__PURE__*/ _jsx("div", {
                    "data-testid": testIds.empty(),
                    children: emptyState
                }),
                pageControls
            ]
        });
    }
    const rowPitch = ROW_HEIGHT + (rowStyle === 'separated' ? SEPARATED_ROW_GAP : 0);
    const rowWindow = virtualized ? virtualRowWindow({
        count: visibleItems.length,
        scrollTop,
        viewportHeight: containerHeight,
        rowPitch,
        overscan: VIRTUAL_OVERSCAN_ROWS,
        unmeasuredRows: VIRTUAL_UNMEASURED_ROWS
    }) : {
        start: 0,
        end: visibleItems.length
    };
    const handleScroll = ()=>{
        onScroll();
        if (virtualized && scrollerRef.current != null) setScrollTop(scrollerRef.current.scrollTop);
    };
    const allSelectableIds = visibleItems.filter((row)=>{
        var _selection_isSelectable;
        return (selection === null || selection === void 0 ? void 0 : (_selection_isSelectable = selection.isSelectable) === null || _selection_isSelectable === void 0 ? void 0 : _selection_isSelectable.call(selection, row)) !== false;
    }).map((row)=>getRowId(row));
    const columnsTrigger = columnsPanel == null ? null : /*#__PURE__*/ _jsx(Row, {
        justifyContent: "end",
        children: /*#__PURE__*/ _jsx(Tooltip, {
            title: columnsPanel.label,
            children: /*#__PURE__*/ _jsx("span", {
                className: styles.columnsTriggerHost,
                children: /*#__PURE__*/ _jsx(DropdownMenu, {
                    target: /*#__PURE__*/ _jsx(IconButton, {
                        bi: columnsPanel.triggerBi,
                        skin: "transparent",
                        size: "smallMedium",
                        className: styles.columnsTrigger,
                        testId: testIds.columnsTrigger(),
                        children: /*#__PURE__*/ _jsx(Settings, {
                            size: SETTINGS_ICON_SIZE
                        })
                    }),
                    testId: testIds.columnsMenu(),
                    children: /*#__PURE__*/ _jsx(ColumnsPanel, {
                        title: columnsPanel.title,
                        frozenLabel: columnsPanel.frozenLabel,
                        entries: columnsPanel.entries,
                        bi: columnsPanel.bi,
                        onToggleVisibility: columnsPanel.onToggleVisibility,
                        onReorder: columnsPanel.onReorder,
                        testId: testIds.columnsPanel()
                    })
                })
            })
        })
    });
    const header = /*#__PURE__*/ _jsx(TableHeader, {
        columns: bodyColumns,
        trailingColumn: resolvedTrailingColumn,
        frozenOffsets: frozenOffsets,
        frozenPrefixLength: frozenPrefixLength,
        isScrolledX: isScrolledX,
        isScrolledY: isScrolledY,
        hasColumnsBeyondEnd: hasColumnsBeyondEnd,
        hasActions: rowActions != null,
        hasSelection: selection != null,
        minWidth: minWidth,
        sort: allowSorting ? effectiveSort : undefined,
        onSortClick: !allowSorting ? undefined : (sort === null || sort === void 0 ? void 0 : sort.sortedBy) === 'table' ? (columnId)=>setTableSort(nextSort(effectiveSort, columnId)) : sort != null ? (columnId)=>sort.onChange(nextSort(sort, columnId)) : undefined,
        selectAll: selection != null ? {
            checked: allSelectableIds.length > 0 && allSelectableIds.every((id)=>selection.selectedIds.has(id)),
            partiallyChecked: allSelectableIds.some((id)=>selection.selectedIds.has(id)),
            onChange: (checked)=>selection.onChange(new Set(checked ? allSelectableIds : [])),
            bi: {
                name: 'select_all_rows'
            }
        } : undefined,
        onResizeStart: allowColumnResize && onColumnsStateChange != null ? interactions.onResizeStart : undefined,
        resizingColumnId: interactions.resizingColumnId,
        onReorder: COLUMN_HEADER_CHROME_ENABLED && allowColumnReorder && onColumnsStateChange != null ? (columnIds)=>onColumnsStateChange(applyDroppedOrder({
                state: columnsState,
                order: columnIds
            })) : undefined,
        reorderBi: reorderBi,
        columnMenu: COLUMN_HEADER_CHROME_ENABLED && onColumnsStateChange != null ? columnMenu : undefined,
        onToggleFrozen: (columnId)=>onColumnsStateChange === null || onColumnsStateChange === void 0 ? void 0 : onColumnsStateChange(applyToggleFrozen({
                state: columnsState,
                resolved,
                columnId
            })),
        onHideColumn: !allowColumnHide ? undefined : (columnId)=>onColumnsStateChange === null || onColumnsStateChange === void 0 ? void 0 : onColumnsStateChange(_object_spread_props(_object_spread({}, columnsState), {
                hidden: [
                    ...columnsState.hidden,
                    columnId
                ]
            })),
        testId: testIds.header()
    });
    const skeletonRowsThatFit = containerHeight > 0 ? Math.max(1, Math.floor(containerHeight / (ROW_HEIGHT + SKELETON_ROW_GAP))) : SKELETON_ROW_COUNT;
    const skeletonRowCount = Math.min((_ref = pagination === null || pagination === void 0 ? void 0 : pagination.pageSize) !== null && _ref !== void 0 ? _ref : skeletonRowsThatFit, skeletonRowsThatFit);
    const table = /*#__PURE__*/ _jsx(TableSurface, {
        scrollerRef: scrollerRef,
        onScroll: handleScroll,
        framed: rowStyle === 'unified',
        hasRowsBelow: hasRowsBelow,
        testId: testId,
        children: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                measurer,
                header,
                effectiveState === 'loading' && Array.from({
                    length: skeletonRowCount
                }, (_, index)=>/*#__PURE__*/ _jsx(Skeleton, {
                        className: styles.skeletonRow,
                        skin: "light",
                        height: ROW_HEIGHT,
                        style: {
                            marginBottom: SKELETON_ROW_GAP,
                            '--dt-skeleton-delay': "".concat(index * SKELETON_ROW_STAGGER_MS, "ms")
                        },
                        testId: testIds.skeleton(index)
                    }, index)),
                effectiveState === 'empty' && /*#__PURE__*/ _jsx("div", {
                    className: styles.emptyState,
                    "data-testid": testIds.empty(),
                    children: emptyState
                }),
                rowWindow.start > 0 && /*#__PURE__*/ _jsx("div", {
                    style: {
                        height: rowWindow.start * rowPitch
                    }
                }),
                effectiveState !== 'loading' && state !== 'empty' && visibleItems.slice(rowWindow.start, rowWindow.end).map((row)=>{
                    var _selection_isSelectable;
                    const rowId = getRowId(row);
                    return /*#__PURE__*/ _jsx(TableRow, {
                        row: row,
                        rowId: rowId,
                        columns: bodyColumns,
                        trailingColumn: resolvedTrailingColumn,
                        rowActions: rowActions,
                        frozenOffsets: frozenOffsets,
                        frozenPrefixLength: frozenPrefixLength,
                        isScrolledX: isScrolledX,
                        virtualizeOptions: virtualized,
                        hasColumnsBeyondEnd: hasColumnsBeyondEnd,
                        minWidth: minWidth,
                        onRowClick: onRowClick,
                        heapId: rowHeapId === null || rowHeapId === void 0 ? void 0 : rowHeapId(row),
                        intent: rowIntent === null || rowIntent === void 0 ? void 0 : rowIntent(row),
                        entering: !!(isRowEntering === null || isRowEntering === void 0 ? void 0 : isRowEntering(row)),
                        hasSelection: selection != null,
                        pending: pendingRowIds.has(rowId),
                        rowStyle: rowStyle,
                        now: now,
                        selection: selection != null ? {
                            isSelected: selection.selectedIds.has(rowId),
                            isSelectable: ((_selection_isSelectable = selection.isSelectable) === null || _selection_isSelectable === void 0 ? void 0 : _selection_isSelectable.call(selection, row)) !== false,
                            onChange: ()=>selection.onChange(toggledSelection(selection.selectedIds, rowId)),
                            bi: {
                                name: 'select_row'
                            }
                        } : undefined,
                        editing: rowEditingFor(row, rowId),
                        rowHref: rowHref === null || rowHref === void 0 ? void 0 : rowHref(row),
                        testId: testIds.row(rowId)
                    }, rowId);
                }),
                rowWindow.end < visibleItems.length && /*#__PURE__*/ _jsx("div", {
                    style: {
                        height: (visibleItems.length - rowWindow.end) * rowPitch
                    }
                })
            ]
        })
    });
    if (columnsTrigger == null && pageControls == null) return table;
    return /*#__PURE__*/ _jsxs(Column, {
        gap: 12,
        className: styles.tableChrome,
        children: [
            columnsTrigger,
            table,
            pageControls
        ]
    });
}
